<template>
  <v-dialog :value="value" @input="close()" scrollable max-width="720" persistent :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card elevation="1">
      <v-card-title class="height-57">
        <span>{{ title }}</span>
        <v-spacer/>
        <v-btn icon @click="close()"><v-icon color="danger">mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider/>
      <v-card-text class="overflow-x-auto">
        <v-row>
          <v-col cols="12" class="pb-0">
            <span class="fn-14">{{ hint }}</span>
          </v-col>
          <v-col cols="12" class="pb-0">
            <a @click="downloadTemplate()"><v-icon size="18" class="mr-1" color="success">mdi-file-excel</v-icon>{{ use_language.download_template }}</a>
          </v-col>
          <v-col cols="12" md="8">
            <v-file-input
              v-model="file"
              accept=".xlsx,.xls,.csv"
              :placeholder="use_language.choose_file"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              outlined
              dense
              hide-details
              @change="readFile"
            ></v-file-input>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <span v-if="rows.length > 0">{{ formatMoney(rows.length) }} {{ use_language.rows }}</span>
          </v-col>
        </v-row>

        <!-- ผลการนำเข้า -->
        <div v-if="result != null" class="mt-3">
          <v-divider/>
          <div class="py-3">
            <b>{{ use_language.import_result }}</b> :
            <span style="color: darkgreen;">{{ use_language.import_success_count }} {{ formatMoney(result.successCount) }} {{ use_language.rows }}</span>
            <span v-if="result.errors.length > 0" class="ml-3" style="color: red;">{{ use_language.import_error_count }} {{ formatMoney(result.errors.length) }} {{ use_language.rows }}</span>
          </div>
          <v-data-table
            v-if="result.errors.length > 0"
            :headers="errorHeaders"
            :items="result.errors"
            :items-per-page="10"
            class="packhai-border-table"
          >
            <!-- row ที่ backend คืนเริ่มที่ 1 ส่วนแถวใน Excel เริ่มที่ 2 เพราะแถวแรกเป็นหัวคอลัมน์ -->
            <template v-slot:item.row="{ item }">{{ item.row + 1 }}</template>
          </v-data-table>
        </div>
      </v-card-text>
      <v-divider/>
      <v-card-actions>
        <v-spacer/>
        <v-btn :elevation="1" outlined class="px-2" @click="close()">{{ use_language.close }}</v-btn>
        <v-btn :elevation="1" color="primary" class="px-2" :disabled="rows.length == 0" :loading="loading" @click="submit()">
          <v-icon size="20" class="mr-1">mdi-upload</v-icon>
          {{ use_language.import_excel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { read, utils, writeFileXLSX } from 'xlsx'
  import { formatMoney } from '@/website/global_function'
  import { getOmsLanguage } from '@/website/backend/oms/oms_language'

  // dialog นำเข้า Excel ที่ใช้ร่วมกันทุกหน้าของ OMS
  // columns: [{ key: ชื่อ field ที่ส่ง API, header: หัวคอลัมน์ในไฟล์ตัวอย่าง, aliases: หัวคอลัมน์อื่นที่ยอมรับ, type: 'number' | 'list' }]
  export default {
    props: {
      value: { type: Boolean, default: false },
      title: { type: String, default: '' },
      hint: { type: String, default: '' },
      columns: { type: Array, required: true },
      templateRows: { type: Array, default: () => [] },
      templateName: { type: String, default: 'oms_template' },
      importFn: { type: Function, required: true }
    },
    data: () => ({
      use_language: getOmsLanguage(),
      file: null,
      rows: [],
      result: null,
      loading: false
    }),
    computed: {
      errorHeaders () {
        return [
          { text: this.use_language.row, align: 'center', value: 'row', sortable: false, width: '90px' },
          { text: this.use_language.reason, align: 'left', value: 'message', sortable: false }
        ]
      }
    },
    watch: {
      value (isOpen) {
        if (isOpen) {
          this.file = null
          this.rows = []
          this.result = null
        }
      }
    },
    methods: {
      formatMoney,
      close () {
        this.$emit('input', false)
      },
      downloadTemplate () {
        const header = this.columns.map(column => column.header)
        const sheet = utils.aoa_to_sheet([header, ...this.templateRows])
        const book = utils.book_new()
        utils.book_append_sheet(book, sheet, 'Sheet1')
        writeFileXLSX(book, this.templateName + '.xlsx')
      },
      async readFile (file) {
        this.rows = []
        this.result = null
        if (file == null) {
          return
        }
        const buffer = await file.arrayBuffer()
        const book = read(buffer, { type: 'array' })
        const sheet = book.Sheets[book.SheetNames[0]]
        const rawRows = utils.sheet_to_json(sheet, { defval: '', raw: false })

        this.rows = rawRows.map(raw => {
          // เทียบหัวคอลัมน์แบบไม่สนตัวพิมพ์และช่องว่างหัวท้าย
          const byHeader = {}
          for (const header in raw) {
            byHeader[String(header).trim().toLowerCase()] = raw[header]
          }
          const row = {}
          for (const column of this.columns) {
            const names = [column.header, ...(column.aliases || [])]
            let cell = ''
            for (const name of names) {
              const found = byHeader[name.trim().toLowerCase()]
              if (found != null && String(found).trim() !== '') {
                cell = String(found).trim()
                break
              }
            }
            if (column.type === 'number') {
              const number = Number(cell.replace(/,/g, ''))
              row[column.key] = cell === '' || isNaN(number) ? -1 : number
            } else if (column.type === 'list') {
              row[column.key] = cell === '' ? [] : cell.split('|').map(text => text.trim()).filter(text => text !== '')
            } else {
              row[column.key] = cell === '' ? null : cell
            }
          }
          return row
        })

        if (this.rows.length == 0) {
          this.$swal({ position: 'top', type: 'warning', text: this.use_language.file_empty })
        }
      },
      async submit () {
        this.loading = true
        const response = await this.importFn(this.rows)
        this.loading = false
        if (response.status !== 'success') {
          this.$swal({
            position: 'top',
            type: 'error',
            text: response.message || (response.errorCode === 'NETWORK' ? this.use_language.network_error : this.use_language.cannot_perform_operation)
          })
          return
        }
        this.result = { successCount: response.successCount, errors: response.errors || [] }
        this.$emit('done', this.result)
      }
    }
  }
</script>
